import { useState } from "react";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";

import { auth } from "../firebase";
import API from "../api";

import { useNavigate } from "react-router-dom";

import { motion } from "framer-motion";

import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  ArrowLeft,
  Zap,
  Globe,
} from "lucide-react";

export default function EmployeeLogin() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);

  // =====================================================
  // 🔐 EMPLOYEE EMAIL LOGIN
  // =====================================================

  const handleLogin = async () => {
    if (!email || !password) {
      alert("Please enter email & password");
      return;
    }

    try {
      setLoading(true);

      // 🔥 LOGIN
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      // 🔥 GET TOKEN
      const token = await userCredential.user.getIdToken(true);

      // 🔥 VERIFY USER
      const res = await API.post(
        "verify-token/",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = res.data;

      console.log("🔥 VERIFY DATA:", data);

      // =====================================================
      // 🔥 CHECK EMPLOYEE ACCESS
      // =====================================================

      const isEmployee = data.organizations?.some(
        (org) => org.role === "employee"
      );

      if (!isEmployee) {
        await signOut(auth);

        alert("Employee access only");
        return;
      }

      // =====================================================
      // 🔥 STORE DATA
      // =====================================================

      const employeeOrg = data.organizations.find(
        (org) => org.role === "employee"
      );

      if (employeeOrg) {
        localStorage.setItem("orgId", employeeOrg.orgId);
        localStorage.setItem("orgSlug", employeeOrg.orgSlug);
        localStorage.setItem("role", employeeOrg.role);
      }

      // =====================================================
      // ✅ SUCCESS
      // =====================================================

      navigate("/redirect");

    } catch (error) {
      console.error(error);

      alert(
        error.response?.data?.error ||
          error.message ||
          "Login Failed"
      );

      await signOut(auth);

    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // 🔐 GOOGLE LOGIN
  // =====================================================

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);

      const provider = new GoogleAuthProvider();

      const result = await signInWithPopup(auth, provider);

      const token = await result.user.getIdToken(true);

      // 🔥 VERIFY
      const res = await API.post(
        "verify-token/",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = res.data;

      const isEmployee = data.organizations?.some(
        (org) => org.role === "employee"
      );

      if (!isEmployee) {
        await signOut(auth);

        alert("Employee access only");

        return;
      }

      const employeeOrg = data.organizations.find(
        (org) => org.role === "employee"
      );

      if (employeeOrg) {
        localStorage.setItem("orgId", employeeOrg.orgId);
        localStorage.setItem("orgSlug", employeeOrg.orgSlug);
        localStorage.setItem("role", employeeOrg.role);
      }

      navigate("/redirect");

    } catch (error) {
      console.error(error);

      alert(
        error.response?.data?.error ||
          error.message ||
          "Google login failed"
      );

      await signOut(auth);

    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // 🔐 FORGOT PASSWORD
  // =====================================================

  const handleForgotPassword = async () => {
    if (!email) {
      alert("Please enter your email first.");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);

      alert("Password reset link sent successfully ✅");

    } catch (error) {
      alert(error.message || "Failed to send reset email.");
    }
  };

  // =====================================================
  // 🎨 UI
  // =====================================================

  return (
    <div className="h-screen flex bg-[#F8FAFC] overflow-hidden">

      {/* ===================================================== */}
      {/* LEFT SIDE */}
      {/* ===================================================== */}

      <div className="hidden lg:flex w-[42%] bg-gradient-to-br from-[#1e1b4b] via-[#312e81] to-[#1e1b4b] flex-col justify-between p-16 text-white relative overflow-hidden shadow-2xl">

        {/* GLOWS */}

        <div className="absolute top-0 right-0 w-full h-full pointer-events-none">

          <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-indigo-500 rounded-full blur-[140px] opacity-20" />

          <div className="absolute top-1/3 -left-20 w-80 h-80 bg-violet-600 rounded-full blur-[120px] opacity-15" />

          <div className="absolute -bottom-32 left-1/4 w-[400px] h-[400px] bg-blue-600 rounded-full blur-[150px] opacity-10" />

        </div>

        {/* CONTENT */}

        <div className="relative z-10">

          {/* LOGO */}

          <div className="flex items-center space-x-4 mb-20">

            <div className="relative">

              <div className="absolute -inset-1 bg-gradient-to-r from-indigo-400 to-violet-500 rounded-lg blur opacity-30"></div>

              <div className="relative bg-white p-2 rounded-lg shadow-sm flex items-center justify-center">

                <img
                  src="/logo.png"
                  alt="Logo"
                  className="w-7 h-7 object-contain"
                />

              </div>

            </div>

            <h1 className="text-2xl font-bold tracking-tight text-white">
              Avaami
              <span className="text-indigo-300 ml-1">
                Pulse
              </span>
            </h1>

          </div>

          {/* HERO */}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >

            <h2 className="text-5xl font-extrabold leading-tight mb-6 tracking-tight">

              Empowering your <br />

              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 via-violet-200 to-blue-200">
                workflow pulse.
              </span>

            </h2>

            <div className="w-12 h-1 bg-indigo-400 rounded-full mb-8 shadow-lg shadow-indigo-500/50" />

            <p className="text-indigo-100/70 text-lg max-w-sm leading-relaxed font-medium">

              A unified space designed for the modern employee to excel, collaborate, and grow.

            </p>

          </motion.div>
        </div>

        {/* FOOTER */}

        <div className="relative z-10 flex gap-10 border-t border-white/10 pt-10">

          <div className="flex items-center gap-3">
            <Zap className="w-5 h-5 text-indigo-300" />

            <span className="text-[11px] font-bold uppercase tracking-widest text-indigo-200">
              High Velocity
            </span>
          </div>

          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-indigo-300" />

            <span className="text-[11px] font-bold uppercase tracking-widest text-indigo-200">
              Enterprise Ready
            </span>
          </div>

        </div>
      </div>

      {/* ===================================================== */}
      {/* RIGHT SIDE */}
      {/* ===================================================== */}

      <div className="w-full lg:w-[58%] flex items-center justify-center px-4 py-6">

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-[500px] bg-white px-5 py-6 rounded-[32px] shadow-[0_20px_50px_rgba(0,0,0,0.04)] border border-slate-100"
        >

          {/* HEADER */}

          <div className="mb-10 text-center">

            <span className="inline-block px-4 py-1.5 bg-indigo-50 text-indigo-700 text-[11px] font-bold uppercase tracking-widest rounded-full mb-6">

              SECURE ACCESS

            </span>

            <h3 className="text-3xl font-extrabold text-slate-900 tracking-tight">
              Welcome Back
            </h3>

            <p className="text-slate-500 mt-2 text-sm font-medium">
              Please enter your details to sign in
            </p>

          </div>

          {/* FORM */}

          <div className="space-y-5">

            {/* EMAIL */}

            <div className="group">

              <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 ml-1">
                Work Email
              </label>

              <div className="relative">

                <Mail
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  size={18}
                />

                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-semibold focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/5 outline-none"
                />

              </div>
            </div>

            {/* PASSWORD */}

            <div className="group">

              <div className="flex justify-between mb-2 px-1">

                <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  Password
                </label>

                <button
                  onClick={handleForgotPassword}
                  className="text-[11px] font-bold text-indigo-600 hover:text-indigo-700"
                >
                  Forgot Password?
                </button>

              </div>

              <div className="relative">

                <Lock
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  size={18}
                />

                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-12 pr-12 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-semibold focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/5 outline-none"
                />

                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>

              </div>
            </div>

            {/* BUTTONS */}

            <div className="pt-4 space-y-4">

              <motion.button
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleLogin}
                disabled={loading}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white py-4 rounded-2xl text-[13px] font-bold uppercase tracking-widest shadow-lg shadow-slate-200 transition-all disabled:opacity-70"
              >

                {loading
                  ? "Authenticating..."
                  : "Sign In to Pulse"}

              </motion.button>

              {/* DIVIDER */}

              <div className="relative flex items-center py-2">

                <div className="flex-grow border-t border-slate-100"></div>

                <span className="flex-shrink mx-4 text-slate-400 text-[10px] font-bold uppercase tracking-widest">
                  Or continue with
                </span>

                <div className="flex-grow border-t border-slate-100"></div>

              </div>

              {/* GOOGLE */}

              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={handleGoogleLogin}
                disabled={loading}
                className="w-full border border-slate-200 py-4 rounded-2xl flex items-center justify-center gap-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
              >

                <img
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  className="w-5 h-5"
                  alt="Google"
                />

                Google Workspace

              </motion.button>

            </div>
          </div>

          {/* BACK */}

          <div className="mt-12 text-center">

            <button
              onClick={() => navigate("/")}
              className="group inline-flex items-center gap-2 text-[11px] font-bold text-slate-400 hover:text-slate-600 uppercase tracking-widest transition-colors"
            >

              <ArrowLeft
                size={14}
                className="group-hover:-translate-x-1 transition-transform"
              />

              Back to Website

            </button>

          </div>
        </motion.div>
      </div>
    </div>
  );
}